import { useCallback, useEffect, useState } from "react";
import { useSession } from "../../state/SessionProvider";
import { confirm } from "../../components/haptics";
import { erEkteProduksjon } from "../../state/session";
import * as stillas from "../../state/stillas";

/**
 * Hvem denne telefonen er, mens vi bygger.
 *
 * ========  DEN FINNES FOR ÉN PROEVE  ========
 *
 * Teamet deler logg, og den flyten kan bare proeves med to enheter som er
 * to ULIKE MEDLEMMER PAA SAMME TEAM. Det krever ikke innlogging — det
 * krever bare at de to er ulike.
 *
 * Alt annet i proeven er ekte: oekt-id-en regnes ut som den skal, postene
 * hentes fra basen, ankrene foelger med, og rapporten sperres av koen.
 * Det eneste som er paafunnet er navnet paa telefonen.
 *
 * Rendres ikke naar innlogging er paa. Da sier `production_member` hvem du
 * er, og basen haandhever det.
 *
 * ========  TEAMET SKRIVES TIL BASEN  ========
 *
 * Ikke bare lokalt. Policyene spoer `production_member.unit_id` om du faar
 * skrive i teamets oekt, og nettflata leser det samme feltet — et team som
 * bare fantes paa telefonen ville gitt to flater som sa ulike ting.
 */
export function StillasPane(): JSX.Element {
  const { production, member, unitId } = useSession();
  const [folk, setFolk] = useState<readonly stillas.Medlem[]>([]);
  const [lag, setLag] = useState<readonly stillas.Lag[]>([]);
  const [valgtLag, setValgtLag] = useState<string | null>(unitId === "" ? null : unitId);
  const [henter, setHenter] = useState(true);

  useEffect(() => {
    void (async () => {
      setFolk(await stillas.hentMedlemmer(production.id));
      setLag(await stillas.hentLag(production.id));
      setHenter(false);
    })();
  }, [production.id]);

  const bli = useCallback((m: stillas.Medlem): void => {
    confirm();
    void stillas.blirMedlem(m, valgtLag);
  }, [valgtLag]);

  if (!erEkteProduksjon(production.id)) {
    return (
      <span className="mono" style={{ fontSize: 12, lineHeight: 1.6, color: "var(--ink-dim)" }}>
        Privat har ingen medlemmer å være. Bytt til en produksjon først.
      </span>
    );
  }

  if (!henter && folk.length === 0) {
    return (
      <span className="mono" style={{ fontSize: 12, lineHeight: 1.6, color: "var(--ink-dim)" }}>
        Krever nett. Lista hentes fra produksjonen.
      </span>
    );
  }

  return (
    <>
      <span className="mono" style={{ fontSize: 12, lineHeight: 1.6, color: "var(--ink-dim)" }}>
        For å prøve at et team deler logg: sett to telefoner til hvert sitt
        medlem på samme team. Alt annet er ekte — også basen. Forsvinner når
        innlogging slås på.
      </span>

      <div className="menu__group">
        <span className="menu__groupname">TEAM</span>
        {lag.map((l) => {
          const valgt = l.id === valgtLag;
          return (
            <button
              key={l.id}
              type="button"
              className="menu__row"
              aria-current={valgt}
              onClick={() => setValgtLag(valgt ? null : l.id)}
            >
              <span>{l.navn}</span>
              <span className="menu__rowvalue">{valgt ? "✓" : ""}</span>
            </button>
          );
        })}
        <span className="menu__readonly">
          Uten team er loggen din egen, som før.
        </span>
      </div>

      <div className="menu__group">
        <span className="menu__groupname">VÆR</span>
        {folk.map((m) => {
          const erMeg = m.id === member.id;
          return (
            <button
              key={m.id}
              type="button"
              className="menu__row"
              aria-current={erMeg}
              onClick={() => bli(m)}
            >
              <span>{m.navn}</span>
              <span className="menu__rowvalue">
                {erMeg ? "✓" : `${m.initialer} · ${m.rolle}`}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}
